import LocalStorageUtils from 'utils/LocalStorage';

const STORE_KEY = 'workplansiq.mockOrganizationStore.v1';
const SELECTED_ORGANIZATION_KEY_PREFIX = 'workplansiq.selectedOrganizationId.';

const readJson = (value, fallback) => {
  try { return value ? JSON.parse(value) : fallback; } catch { return fallback; }
};

const normalizeEmail = (value) => String(value || '').trim().toLowerCase();

const buildUserId = (email) => `user-${normalizeEmail(email).replace(/[^a-z0-9]+/g, '-') || 'anonymous'}`;

export const getCurrentMockUser = () => {
  const authUser = LocalStorageUtils.userInfo.get() || {};
  const email = normalizeEmail(
    sessionStorage.getItem('workplansiq.signedInEmail')
      || authUser.email
      || authUser.emailAddress
      || authUser.preferred_username
      || authUser.username,
  );
  const id = String(authUser.userId || authUser.id || buildUserId(email));

  return {
    id,
    email,
    name: authUser.userFullName || authUser.fullName || authUser.name || email || 'WorkPlansIQ User',
  };
};

const emptyStore = () => ({
  currentUser: null,
  selectedOrganizationId: null,
  users: [],
  organizations: [],
  memberships: [],
  invitations: [],
  accessRequests: [],
  subscriptions: [],
  billingAccounts: [],
  invoices: [],
});

export const readMockOrganizationStore = () => {
  const stored = readJson(
    sessionStorage.getItem(STORE_KEY) || localStorage.getItem(STORE_KEY),
    emptyStore(),
  );
  return {
    currentUser: stored.currentUser || null,
    selectedOrganizationId: stored.selectedOrganizationId || null,
    users: Array.isArray(stored.users) ? stored.users : [],
    organizations: Array.isArray(stored.organizations) ? stored.organizations : [],
    memberships: Array.isArray(stored.memberships) ? stored.memberships : [],
    invitations: Array.isArray(stored.invitations) ? stored.invitations : [],
    accessRequests: Array.isArray(stored.accessRequests) ? stored.accessRequests : [],
    subscriptions: Array.isArray(stored.subscriptions) ? stored.subscriptions : [],
    billingAccounts: Array.isArray(stored.billingAccounts) ? stored.billingAccounts : [],
    invoices: Array.isArray(stored.invoices) ? stored.invoices : [],
  };
};

const synchronizeCommercialEntities = (store, organization) => {
  const organizationId = organization.id;
  const hasPlan = Boolean(organization.plan && organization.plan !== 'Plan not selected');
  const subscriptionId = `subscription-${organizationId}`;
  const billingAccountId = `billing-${organizationId}`;
  const invoiceId = `invoice-${organizationId}`;

  store.subscriptions = store.subscriptions.filter((item) => item.organizationId !== organizationId);
  store.billingAccounts = store.billingAccounts.filter((item) => item.organizationId !== organizationId);
  store.invoices = store.invoices.filter((item) => item.organizationId !== organizationId);
  if (!hasPlan) return;

  store.subscriptions.push({
    id: subscriptionId,
    organizationId,
    plan: organization.plan,
    planCode: organization.planCode || null,
    capacity: organization.planCapacity || null,
    durationMonths: organization.planDurationMonths || null,
    status: organization.subscriptionStatus || 'PLAN_DRAFT',
    accessStatus: organization.accessStatus || 'EXPLORATION',
  });

  const hasBilling = Boolean(
    organization.billingAccount
    || organization.invoiceNumber
    || !['NOT_STARTED', undefined, null].includes(organization.paymentStatus),
  );
  if (!hasBilling) return;

  store.billingAccounts.push({
    id: billingAccountId,
    organizationId,
    subscriptionId,
    status: organization.paymentStatus || 'NOT_STARTED',
    ...(organization.billingAccount || {}),
  });

  const hasInvoice = Boolean(
    organization.invoiceNumber
    || ['PENDING_PAYMENT', 'PAID'].includes(organization.paymentStatus),
  );
  if (!hasInvoice) return;

  store.invoices.push({
    id: invoiceId,
    organizationId,
    subscriptionId,
    billingAccountId,
    invoiceNumber: organization.invoiceNumber || `INV-${organizationId}`,
    status: organization.paymentStatus || 'PENDING_PAYMENT',
    issuedAt: organization.invoiceIssuedAt || null,
    dueAt: organization.paymentDueAt || null,
    paidAt: organization.paidAt || null,
  });
};

export const saveMockOrganizationStore = (store) => {
  const serialized = JSON.stringify(store);
  sessionStorage.setItem(STORE_KEY, serialized);
  localStorage.setItem(STORE_KEY, serialized);
  return store;
};

export const ensureCurrentMockUser = () => {
  const user = getCurrentMockUser();
  const store = readMockOrganizationStore();
  const userChanged = store.currentUser?.id !== user.id;
  const existing = store.users.find((item) => item.id === user.id);
  store.currentUser = user;
  if (userChanged) {
    store.selectedOrganizationId = localStorage.getItem(`${SELECTED_ORGANIZATION_KEY_PREFIX}${user.id}`) || null;
  }
  store.users = existing
    ? store.users.map((item) => item.id === user.id ? { ...item, ...user } : item)
    : [...store.users, user];
  saveMockOrganizationStore(store);
  return user;
};

export const getSelectedOrganizationId = () => {
  const user = ensureCurrentMockUser();
  const store = readMockOrganizationStore();
  return store.currentUser?.id === user.id ? store.selectedOrganizationId : null;
};

export const setSelectedOrganizationId = (organizationId) => {
  const user = ensureCurrentMockUser();
  const store = readMockOrganizationStore();
  store.currentUser = user;
  store.selectedOrganizationId = organizationId || null;
  const selectionKey = `${SELECTED_ORGANIZATION_KEY_PREFIX}${user.id}`;
  if (organizationId) localStorage.setItem(selectionKey, organizationId);
  else localStorage.removeItem(selectionKey);
  saveMockOrganizationStore(store);
  return store.selectedOrganizationId;
};

export const replaceCurrentUserOrganizations = (tenantViews) => {
  const user = ensureCurrentMockUser();
  const store = readMockOrganizationStore();
  const views = Array.isArray(tenantViews) ? tenantViews : [];
  const organizationIds = new Set(views.map((item) => item.id));

  const organizations = views.map(({ role, roleId, membershipId, membershipStatus, permissions, userId, organizationId, ...organization }) => ({
    ...organization,
    setupStatus: organization.workspaceSetupStatus || organization.setupStatus || 'PENDING',
  }));
  const memberships = views.map((item) => ({
    id: item.membershipId || `membership-${user.id}-${item.id}`,
    userId: user.id,
    organizationId: item.id,
    role: item.role || 'Member',
    roleId: item.roleId || null,
    status: item.membershipStatus || 'ACTIVE',
    permissions: Array.isArray(item.permissions) ? item.permissions : [],
  }));

  store.organizations = [
    ...store.organizations.filter((item) => !organizationIds.has(item.id)),
    ...organizations,
  ];
  store.memberships = [
    ...store.memberships.filter((item) => item.userId !== user.id),
    ...memberships,
  ];
  organizations.forEach((organization) => synchronizeCommercialEntities(store, organization));
  saveMockOrganizationStore(store);
  return listCurrentUserOrganizationViews();
};

export const upsertOrganizationForCurrentUser = (tenantView) => {
  const user = ensureCurrentMockUser();
  const store = readMockOrganizationStore();
  const {
    role, roleId, membershipId, membershipStatus, permissions, userId, organizationId, ...organizationFields
  } = tenantView;
  const organization = {
    ...organizationFields,
    setupStatus: organizationFields.workspaceSetupStatus || organizationFields.setupStatus || 'PENDING',
  };
  const membership = {
    id: membershipId || `membership-${user.id}-${organization.id}`,
    userId: user.id,
    organizationId: organization.id,
    role: role || 'Member',
    roleId: roleId || null,
    status: membershipStatus || 'ACTIVE',
    permissions: Array.isArray(permissions) ? permissions : [],
  };

  store.organizations = store.organizations.some((item) => item.id === organization.id)
    ? store.organizations.map((item) => item.id === organization.id ? { ...item, ...organization } : item)
    : [...store.organizations, organization];
  store.memberships = store.memberships.some((item) => item.id === membership.id)
    ? store.memberships.map((item) => item.id === membership.id ? { ...item, ...membership } : item)
    : [...store.memberships, membership];
  synchronizeCommercialEntities(store, organization);
  saveMockOrganizationStore(store);
  return { ...organization, ...membership, id: organization.id, membershipId: membership.id, membershipStatus: membership.status };
};

export const listCurrentUserOrganizationViews = () => {
  const user = ensureCurrentMockUser();
  const store = readMockOrganizationStore();
  return store.memberships
    .filter((membership) => membership.userId === user.id && membership.status === 'ACTIVE')
    .map((membership) => {
      const organization = store.organizations.find((item) => item.id === membership.organizationId);
      if (!organization) return null;
      return {
        ...organization,
        role: membership.role,
        roleId: membership.roleId,
        membershipId: membership.id,
        membershipStatus: membership.status,
        permissions: membership.permissions || [],
      };
    })
    .filter(Boolean);
};

export const listCurrentUserInvitations = () => {
  const user = ensureCurrentMockUser();
  return readMockOrganizationStore().invitations.filter(
    (invitation) => normalizeEmail(invitation.inviteeEmail || invitation.email) === user.email,
  );
};

export const saveMockInvitation = (invitation) => {
  const store = readMockOrganizationStore();
  const normalized = {
    ...invitation,
    invitationId: invitation.invitationId || invitation.id,
    inviteeEmail: normalizeEmail(invitation.inviteeEmail || invitation.email),
    status: invitation.status || 'PENDING',
  };
  delete normalized.id;
  delete normalized.email;
  store.invitations = store.invitations.some((item) => item.invitationId === normalized.invitationId)
    ? store.invitations.map((item) => item.invitationId === normalized.invitationId ? { ...item, ...normalized } : item)
    : [...store.invitations, normalized];
  saveMockOrganizationStore(store);
  return normalized;
};

export const saveMockAccessRequest = (accessRequest) => {
  const store = readMockOrganizationStore();
  const normalized = {
    ...accessRequest,
    status: accessRequest.status || 'PENDING',
  };
  store.accessRequests = store.accessRequests.some((item) => item.requestId === normalized.requestId)
    ? store.accessRequests.map((item) => item.requestId === normalized.requestId ? { ...item, ...normalized } : item)
    : [...store.accessRequests, normalized];
  saveMockOrganizationStore(store);
  return normalized;
};

export const getMockCommercialContext = (organizationId) => {
  const store = readMockOrganizationStore();
  return {
    subscription: store.subscriptions.find((item) => item.organizationId === organizationId) || null,
    billingAccount: store.billingAccounts.find((item) => item.organizationId === organizationId) || null,
    invoice: store.invoices.find((item) => item.organizationId === organizationId) || null,
  };
};

export const seedCurrentUserMockScenario = ({ organizations = [], memberships = [], invitations = [] }) => {
  const user = ensureCurrentMockUser();
  const store = readMockOrganizationStore();
  const scenarioOrganizationIds = new Set(organizations.map((item) => item.id));
  const oldScenarioOrganizationIds = new Set(
    store.organizations.filter((item) => item.source === 'demo-scenario').map((item) => item.id),
  );

  store.organizations = [
    ...store.organizations.filter((item) => !oldScenarioOrganizationIds.has(item.id) && !scenarioOrganizationIds.has(item.id)),
    ...organizations,
  ];
  store.memberships = [
    ...store.memberships.filter((item) => item.userId !== user.id && !oldScenarioOrganizationIds.has(item.organizationId)),
    ...memberships.map((item) => ({ ...item, userId: user.id, status: item.status || 'ACTIVE', permissions: item.permissions || [] })),
  ];
  store.invitations = [
    ...store.invitations.filter((item) => !(item.source === 'demo-scenario' && item.inviteeEmail === user.email)),
    ...invitations,
  ];
  store.subscriptions = store.subscriptions.filter((item) => !oldScenarioOrganizationIds.has(item.organizationId));
  store.billingAccounts = store.billingAccounts.filter((item) => !oldScenarioOrganizationIds.has(item.organizationId));
  store.invoices = store.invoices.filter((item) => !oldScenarioOrganizationIds.has(item.organizationId));
  organizations.forEach((organization) => synchronizeCommercialEntities(store, organization));
  store.currentUser = user;
  store.selectedOrganizationId = null;
  localStorage.removeItem(`${SELECTED_ORGANIZATION_KEY_PREFIX}${user.id}`);
  saveMockOrganizationStore(store);
  return { user, organizations, memberships, invitations };
};

export const MOCK_ORGANIZATION_STORE_KEY = STORE_KEY;
export const MOCK_SELECTED_ORGANIZATION_KEY_PREFIX = SELECTED_ORGANIZATION_KEY_PREFIX;
